import { motion, useReducedMotion } from 'framer-motion'
import { Link } from 'react-router-dom'
import { cn } from '@/lib/utils'
import { useBookingUrl } from '@/hooks/useBookingUrl'

const MotionLink = motion.create(Link)

type BookingButtonProps = {
  children?: React.ReactNode
  className?: string
  variant?: 'primary' | 'secondary' | 'pill' | 'onDark'
}

export function BookingButton({
  children = 'Book now',
  className,
  variant = 'primary',
}: BookingButtonProps) {
  const href = useBookingUrl()
  const reduced = useReducedMotion()

  const base = cn(
    'inline-flex min-h-11 min-w-[13.5rem] cursor-pointer items-center justify-center px-7 text-xs font-bold uppercase tracking-[0.2em] transition-colors duration-200',
    'rounded-[var(--radius-button)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary'
  )

  const variants = {
    primary:
      'border-2 border-primary bg-primary text-on-primary hover:bg-primary-active active:bg-primary-active',
    secondary:
      'border-2 border-ink bg-transparent text-ink hover:border-primary hover:text-primary hover:bg-canvas-muted active:bg-surface-strong',
    pill:
      'min-h-10 rounded-[var(--radius-pill)] border-0 bg-primary px-6 text-[11px] font-bold uppercase tracking-[0.18em] text-on-primary hover:bg-primary-active active:bg-primary-active md:text-xs',
    onDark:
      'border-2 border-on-olive-soft/35 bg-on-olive text-olive hover:border-canvas hover:bg-canvas hover:text-ink active:bg-canvas-muted',
  }

  return (
    <MotionLink
      to={href}
      className={cn(base, variants[variant], className)}
      whileTap={reduced ? undefined : { scale: 0.98 }}
      transition={{ duration: 0.15 }}
    >
      {children}
    </MotionLink>
  )
}
